import { AnimatePresence, motion } from 'framer-motion';
import { Check, ChevronDown, ChevronUp, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Icon } from '@/components/ui/icon';
import { isBctStaff, useAuthStore } from '@/stores/auth';

// ============================================================================
// Types
// ============================================================================

interface ChecklistItem {
  id: string;
  labelKey: string;
  icon: string;
  /** Route prefix that auto-completes this item */
  routeMatch: string;
}

interface OnboardingChecklistProps {
  completedItems: Record<string, boolean>;
  onCompleteItem: (itemId: string) => void;
  onDismiss: () => void;
}

// ============================================================================
// Constants
// ============================================================================

const CHECKLIST_ITEMS: ChecklistItem[] = [
  {
    id: 'view-dashboard',
    labelKey: 'checklist.viewDashboard',
    icon: 'dashboard',
    routeMatch: '/dashboard',
  },
  {
    id: 'open-project',
    labelKey: 'checklist.openProject',
    icon: 'view_kanban',
    routeMatch: '/projects/',
  },
  {
    id: 'create-issue',
    labelKey: 'checklist.createIssue',
    icon: 'bug_report',
    routeMatch: '/issues',
  },
  {
    id: 'view-documents',
    labelKey: 'checklist.viewDocuments',
    icon: 'description',
    routeMatch: '/documents',
  },
  {
    id: 'run-test',
    labelKey: 'checklist.runTest',
    icon: 'assignment_turned_in',
    routeMatch: '/test-plans',
  },
];

// ============================================================================
// Component
// ============================================================================

export function OnboardingChecklist({
  completedItems,
  onCompleteItem,
  onDismiss,
}: OnboardingChecklistProps) {
  const { t } = useTranslation('help');
  const { user } = useAuthStore();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);

  const shouldHide = !user || isBctStaff(user.role) || completedItems.dismissed;
  const completedCount = CHECKLIST_ITEMS.filter((item) => completedItems[item.id]).length;
  const allComplete = completedCount === CHECKLIST_ITEMS.length;

  // Auto-complete items based on route visits
  useEffect(() => {
    if (shouldHide) return;
    for (const item of CHECKLIST_ITEMS) {
      if (!completedItems[item.id] && location.pathname.startsWith(item.routeMatch)) {
        onCompleteItem(item.id);
      }
    }
  }, [location.pathname, completedItems, onCompleteItem, shouldHide]);

  // Auto-dismiss after all items complete (with delay)
  useEffect(() => {
    if (shouldHide) return;
    if (allComplete) {
      const timer = setTimeout(onDismiss, 5000);
      return () => clearTimeout(timer);
    }
  }, [allComplete, onDismiss, shouldHide]);

  if (shouldHide) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      className="fixed bottom-4 right-4 z-40 w-72 rounded-lg border border-border bg-card shadow-lg"
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <div className="flex items-center gap-2">
          <Icon name="checklist" className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium">{t('checklist.title')}</span>
          <span className="text-xs text-muted-foreground">
            {completedCount}/{CHECKLIST_ITEMS.length}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="rounded p-1 text-muted-foreground hover:text-foreground"
            aria-label={collapsed ? 'Expand' : 'Collapse'}
          >
            {collapsed ? (
              <ChevronUp className="h-3.5 w-3.5" />
            ) : (
              <ChevronDown className="h-3.5 w-3.5" />
            )}
          </button>
          <button
            onClick={onDismiss}
            className="rounded p-1 text-muted-foreground hover:text-foreground"
            aria-label="Dismiss checklist"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-1 bg-muted">
        <motion.div
          className="h-full bg-primary"
          initial={{ width: 0 }}
          animate={{ width: `${(completedCount / CHECKLIST_ITEMS.length) * 100}%` }}
          transition={{ duration: 0.3 }}
        />
      </div>

      {/* Items */}
      <AnimatePresence>
        {!collapsed && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="p-3 space-y-1">
              {CHECKLIST_ITEMS.map((item) => {
                const done = !!completedItems[item.id];
                return (
                  <div
                    key={item.id}
                    className="flex items-center gap-3 rounded-md px-2 py-1.5 text-sm"
                  >
                    <div
                      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition-colors ${
                        done
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-muted-foreground/30'
                      }`}
                    >
                      {done && <Check className="h-3 w-3" />}
                    </div>
                    <span className={done ? 'text-muted-foreground line-through' : ''}>
                      {t(item.labelKey)}
                    </span>
                  </div>
                );
              })}
            </div>

            {allComplete && (
              <div className="border-t border-border px-4 py-3 text-center">
                <p className="text-xs text-muted-foreground">{t('checklist.allComplete')}</p>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
